import { Trans, Select, select } from '@lingui/macro';
import classnames from 'classnames';
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import nl2ReactBR from 'react-nl2br';

import Image from 'common/Image/Image';
import RatingStars from 'common/RatingStars/RatingStars';
import { truncateWithEllipsis } from 'common/helpers/helpers';
import isMobile from 'common/helpers/platformDetector';
import Avatar from 'uikit/Avatar/Avatar';
import { TextP3 } from 'uikit/Text';
import routes from 'utils/routeTranslator';

import { Feedback$Props } from './FeedbackCard.helpers';
import './FeedbackCard.scss';

const LINE_HEIGHT = 22;
const MAX_LINES_NUMBER = isMobile.isAnyMobile() ? 6 : 2;
const MAX_COMMENT_HEIGHT = LINE_HEIGHT * MAX_LINES_NUMBER;
const TRUNCATION_LENGTH = 250;

const FeedbackCard: React.FC<Feedback$Props> = (props) => {
  const [isExpandable, setIsExpandable] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const commentEl = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (props.expandable) {
      checkQuoteHeight();
    }
  }, [props.expandable]);

  const checkQuoteHeight = () => {
    const commentHeight = commentEl.current ? commentEl.current.getBoundingClientRect().height : 0;

    setIsExpandable(commentHeight > MAX_COMMENT_HEIGHT);
  };

  const expandComment = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };

  const { user, provider, comment, value, showRatedLabel, category } = props;

  const userCN = classnames('feedbackCard__user', {
    'feedbackCard__user_expandable': isExpandable,
    'feedbackCard__user_expanded': isExpanded,
  });

  const content = !isExpandable || isExpanded ? comment : truncateWithEllipsis(comment, TRUNCATION_LENGTH);

  let gender = '';
  if (user.gender === '1') gender = 'male';
  if (user.gender === '2') gender = 'female';

  const ratingLabel = select(`_${value}`, {
    _1: 'Fatalny',
    _2: 'Zły',
    _3: 'OK',
    _4: 'Dobry',
    _5: 'Doskonały!',
  });

  return (
    <div className="feedbackCard">
      {showRatedLabel && (
        <p className="feedbackCard__rated">
          <Select
            value={gender}
            male={`${user.name} ocenił ${provider.name}`}
            female={`${user.name} oceniła ${provider.name}`}
            other=""
          ></Select>
        </p>
      )}
      <div className="feedbackCard__wrap">
        <div className="feedbackCard__providerLinkWrapper">
          <a
            className="feedbackCard__provider"
            href={provider.slug ? routes.get('front::user.profile', { username: provider.slug }) : undefined}
          >
            {provider.avatar && (
              <Image
                src={provider.avatar}
                size="s"
                webp
                defaultJPEG
                multiPixelRatios
                alt={provider.name}
                className="feedbackCard__providerAvatar"
                lazy
              />
            )}
            <div className="feedbackCard__providerContacts">
              <p className="feedbackCard__providerName">{provider.name}</p>
              <p className="feedbackCard__providerInfo">
                <span>
                  <Trans>Wykonawca</Trans>
                </span>
                ,<span> {provider.city}</span>
              </p>
            </div>
          </a>
        </div>

        {comment && (
          <div className={userCN}>
            <div className="feedbackCard__userInfo">
              <Avatar name={user.name} size={24} avatarUrl={user.avatar} className="feedbackCard__userAvatar" />
              <TextP3 className="feedbackCard__userContacts">
                <strong>{user.name}</strong>
                {user.city && <span> · {user.city}</span>}
              </TextP3>
            </div>

            {value >= 4 && (
              <div className="feedbackCard__rating">
                {category && category.name && (
                  <a href={routes.get('front::category', { slug: category.slug })} className="feedbackCard__category">
                    <TextP3 bold>{category.name}</TextP3>
                  </a>
                )}

                <RatingStars rating={value} size="small" />
                {!category || (!category.name && <span className="feedbackCard__ratingLabel">{ratingLabel}</span>)}
              </div>
            )}

            <div className="feedbackCard__userQuoteWrap">
              <span className="feedbackCard__userQuote" ref={commentEl}>
                {nl2ReactBR(content)}
              </span>
              <button type="button" className="feedbackCard__expand" onClick={expandComment}>
                <Trans>Pokaż więcej</Trans>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

FeedbackCard.defaultProps = {
  expandable: true,
};

export default FeedbackCard;
